import { useState } from 'react';
import styles from './AppFooter.module.css';
import { usePresence, type GeoStat } from '@/hooks/usePresence';
import { countryName, countryFlag } from '@/utils/countries';

const BUILD_NUMBER = import.meta.env.VITE_BUILD_NUMBER;
const VERSION = BUILD_NUMBER
  ? `v4.${String(BUILD_NUMBER).padStart(4, '0')}`
  : 'v4.dev';

const SOURCES = [
  { label: 'EUR-Lex — AMLD', url: 'https://eur-lex.europa.eu/eli/dir/2024/1640/oj/eng' },
  { label: 'EUR-Lex — AMLR', url: 'https://eur-lex.europa.eu/eli/reg/2024/1624/oj/eng' },
  { label: 'EUR-Lex — AMLAR', url: 'https://eur-lex.europa.eu/eli/reg/2024/1620/oj/eng' },
  { label: 'EUR-Lex — TFR', url: 'https://eur-lex.europa.eu/eli/reg/2023/1113/oj/eng' },
  { label: 'AMLA — Regulatory instruments', url: 'https://www.amla.europa.eu/policy/regulatory-instruments_en' },
  { label: 'AMLA — Document library', url: 'https://www.amla.europa.eu/resources/document-library_en' },
  { label: 'AMLA — Public consultations', url: 'https://www.amla.europa.eu/policy/public-consultations_en' },
  { label: 'AMLA — Annex XI SPD 2026-2028', url: 'https://www.amla.europa.eu/policy/regulatory-instruments_en' },
  { label: 'EBA — Travel Rule GL/2024/11', url: 'https://www.eba.europa.eu/activities/single-rulebook/regulatory-activities/anti-money-laundering-and-counter-terrorist-financing' },
];

/** Sorted [code, count] pairs, descending. */
function geoRows(g: GeoStat): [string, number][] {
  return Object.entries(g.geo).sort((a, b) => b[1] - a[1]);
}

function GeoList({ stat, live }: { stat: GeoStat; live?: boolean }) {
  const rows = geoRows(stat);
  const max = rows.length ? rows[0][1] : 1;
  if (!rows.length) {
    return <p className={styles.geoEmpty}>No data yet.</p>;
  }
  return (
    <div className={styles.geoList}>
      {rows.map(([code, n]) => (
        <div className={styles.geoRow} key={code}>
          <span className={styles.country}>
            <span className={styles.flag}>{countryFlag(code)}</span>
            <span className={styles.iso}>{code}</span>
            {countryName(code)}
          </span>
          <span className={`${styles.geoBarWrap} ${live ? styles.live : ''}`}>
            <span className={styles.bar} style={{ width: `${Math.max(6, Math.round((n / max) * 100))}%` }} />
          </span>
          <span className={styles.cnt}>{n.toLocaleString('en-US')}</span>
        </div>
      ))}
    </div>
  );
}

export function AppFooter() {
  const [showSources, setShowSources] = useState(false);
  const [showPresence, setShowPresence] = useState(false);
  const { snapshot, connected } = usePresence();

  const liveCount = snapshot?.live.total ?? 0;

  return (
    <>
      <footer className={styles.footer}>
        <div className={styles.left}>
          <span className={styles.disclaimer}>
            AML Package Navigator — navigational tool only. Always verify against official EUR-Lex and AMLA sources.
          </span>
        </div>
        <div className={styles.right}>
          {connected && snapshot && (
            <button
              className={styles.usersBtn}
              data-tour="presence"
              onClick={() => setShowPresence(true)}
              title="Currently connected — click for details"
            >
              <span className={styles.liveDot} />
              <span className={styles.nUsers}>{liveCount.toLocaleString('en-US')}</span>
              {' '}users
            </button>
          )}
          <span className={styles.credits}>
            Curated by{' '}
            <a href="mailto:erik.saponara@generali.com" className={styles.creditsLink}>
              Erik Saponara
            </a>
            {' '}· GIH AFC Officer Function · AI assisted · <span className={styles.version}>{VERSION}</span>
          </span>
          <button
            className={styles.sourcesBtn}
            onClick={() => setShowSources(true)}
          >
            Sources ↗
          </button>
        </div>
      </footer>

      {showSources && (
        <div className={styles.overlay} onClick={() => setShowSources(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <span>Official Sources</span>
              <button onClick={() => setShowSources(false)}>×</button>
            </div>
            <div className={styles.modalBody}>
              {SOURCES.map((s, i) => (
                <a
                  key={i}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.sourceRow}
                >
                  <span>{s.label}</span>
                  <span className={styles.sourceArrow}>↗</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {showPresence && snapshot && (
        <div className={styles.overlay} onClick={() => setShowPresence(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <span>Live connections</span>
              <button onClick={() => setShowPresence(false)}>×</button>
            </div>
            <div className={styles.modalBody}>
              <section className={styles.presenceSection}>
                <div className={styles.presenceHead}>
                  <span className={styles.liveDot} />
                  <span className={styles.presenceLabel}>Currently connected</span>
                </div>
                <div className={`${styles.bignum} ${styles.bignumLive}`}>
                  <span className={styles.bignumV}>{snapshot.live.total.toLocaleString('en-US')}</span>
                  <span className={styles.bignumU}>users online now</span>
                </div>
                <p className={styles.geoLabel}>By geography</p>
                <GeoList stat={snapshot.live} live />
              </section>

              <section className={styles.presenceSection}>
                <div className={styles.presenceHead}>
                  <span className={styles.presenceLabel}>Overall — all time</span>
                </div>
                <div className={`${styles.bignum} ${styles.bignumTotal}`}>
                  <span className={styles.bignumV}>{snapshot.overall.total.toLocaleString('en-US')}</span>
                  <span className={styles.bignumU}>total connections</span>
                </div>
                <p className={styles.geoLabel}>By geography</p>
                <GeoList stat={snapshot.overall} />
              </section>
            </div>
            <div className={styles.presenceFoot}>
              Aggregated counts only — no IP addresses, no identifiers, no per-user tracking. Country from the Cloudflare edge.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
